import classNames from 'classnames'
import * as React from 'react'
import {render} from 'storyblok-rich-text-react-renderer'

import {makeEditable, unwrapAllNodeResolvers} from '@/common/utils'
import Buttons from '@/components/Buttons'
import Content from '@/components/Content'
import Social from '@/components/Footer/Social'
import {Grid} from '@/components/Grid'
import Images from '@/components/Images'
import Kicker from '@/components/Kicker'
import Testimonial from '@/components/Testimonial'

const ContentColumns = ({
  backgroundColors,
  isReversed,
  style,
  id,
  headingTag,
  kicker,
  heading,
  content,
  testimonial,
  buttons,
  images,
  videoHevc,
  videoWebm,
  socials = [],
  _editable,
}) => {
  const HeadingTag = headingTag || 'p'

  const hasVideo = (videoHevc && videoHevc.filename) || (videoWebm && videoWebm.filename)

  return (
    <section
      id={id ? id : undefined}
      {...makeEditable(_editable)}
      className={classNames(
        {
          'bg-[radial-gradient(circle_at_left,#f6ff8cab,transparent,transparent,transparent,transparent)]':
            isReversed,
        },
        {
          'bg-[radial-gradient(circle_at_right,#f6ff8cab,transparent,transparent,transparent,transparent)]':
            !isReversed,
        },
        backgroundColors.current
      )}
    >
      <Grid
        className={classNames('grid-flow-dense items-center gap-y-6 py-6 md:py-12 lg:py-18', {
          'md:gap-x-8 lg:gap-x-8': style === 'hasBackgroundColor',
        })}
      >
        <div
          className={classNames('col-span-full space-y-8 lg:col-span-5', {
            ' lg:col-start-8': isReversed,
            'rounded-[theme(spacing.10)] bg-violet-200 p-10 md:col-span-full lg:col-span-7':
              style === 'hasBackgroundColor',
          })}
        >
          <HeadingTag className='flex flex-col gap-2'>
            <Kicker kicker={kicker} />
            <span
              className={classNames('heading-s-millik text-purple-900', {
                'heading-xs-satoshi': style === 'hasBackgroundColor',
              })}
            >
              {render(heading, {nodeResolvers: unwrapAllNodeResolvers})}
            </span>
          </HeadingTag>

          <Content className='prose' size='m' content={content} />

          {socials.length > 0 && (
            <ul className='flex flex-wrap gap-6'>
              {socials.map((social) => (
                <Social key={social._uid} {...social} size='small' />
              ))}
            </ul>
          )}

          {testimonial && (
            <div key={testimonial.uuid} className='hidden lg:block'>
              <Testimonial {...testimonial.content} />
            </div>
          )}

          <Buttons buttons={buttons} />
        </div>

        <div
          className={classNames(
            'col-span-full ',
            {
              'md:col-start-2 md:col-end-6 lg:col-span-5 lg:col-start-8': !isReversed,
              'md:col-start-2 md:col-end-6 lg:col-span-5': isReversed,
              'relative hidden rounded-3xl lg:col-span-4 lg:col-start-8 lg:flex':
                style === 'hasBackgroundColor',
            },
            hasVideo ? 'aspect-w-1 aspect-h-1' : 'aspect-w-4 aspect-h-5'
          )}
        >
          {!hasVideo && (
            <Images
              images={images}
              imageProps={{
                fitIn: true,
                ratio: '4:5',
                sizes: {sm: 50, lg: 100},
              }}
              className='aspect-w-4 aspect-h-5'
            />
          )}

          {hasVideo && (
            <video autoPlay loop muted playsInline preload='auto'>
              <source src={videoHevc.filename} type='video/mp4; codecs="hvc1"' />
              <source src={videoWebm.filename} type='video/webm' />
            </video>
          )}
        </div>
      </Grid>
    </section>
  )
}

export default ContentColumns
